import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import userService from '../../services/userService';
import { useAuth } from '../../hooks/useAuth';
import './AdminUsersPage.css';

// Backend uses UPPERCASE enum values
const ROLES = [
  { value: 'SUPER_ADMIN', label: 'Super Admin', color: '#dc2626' },
  { value: 'ADMIN', label: 'Admin', color: '#7c3aed' },
  { value: 'EDITOR', label: 'Editor', color: '#2563eb' },
  { value: 'VIEWER', label: 'Viewer', color: '#059669' },
];

const STATUSES = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Password change modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // User statistics state
  const [stats, setStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Sync status state (SUPER_ADMIN only)
  const [syncStatus, setSyncStatus] = useState(null);
  const [isLoadingSyncStatus, setIsLoadingSyncStatus] = useState(true);

  // Token management state (SUPER_ADMIN only)
  const [isCleaningTokens, setIsCleaningTokens] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenUser, setTokenUser] = useState(null);
  const [revokedTokenCount, setRevokedTokenCount] = useState(null);
  const [isLoadingTokenCount, setIsLoadingTokenCount] = useState(false);

  // Get current user for role check
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'EDITOR',
    status: 'ACTIVE',
  });

  useEffect(() => {
    fetchUsers();
    fetchStats();
    if (isSuperAdmin) {
      fetchSyncStatus();
    }
  }, [page, isSuperAdmin]);

  const fetchStats = async () => {
    try {
      const response = await userService.getStats();
      setStats(response.data?.data || response.data);
    } catch (err) {
      console.error('Error fetching user stats:', err);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await userService.getSyncStatus();
      setSyncStatus(response.data?.data || response.data);
    } catch (err) {
      console.error('Error fetching sync status:', err);
    } finally {
      setIsLoadingSyncStatus(false);
    }
  };

  const formatSyncDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Token management handlers (SUPER_ADMIN only)
  const handleCleanupTokens = async () => {
    if (!window.confirm('Are you sure you want to clean up all expired tokens? This action cannot be undone.')) {
      return;
    }

    setIsCleaningTokens(true);
    try {
      await userService.cleanupExpiredTokens();
      toast.success('Expired tokens cleaned up successfully');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to clean up tokens';
      toast.error(errorMessage);
    } finally {
      setIsCleaningTokens(false);
    }
  };

  const handleViewRevokedTokens = async (user) => {
    setTokenUser(user);
    setRevokedTokenCount(null);
    setShowTokenModal(true);
    setIsLoadingTokenCount(true);

    try {
      const response = await userService.getRevokedTokenCount(user.id);
      setRevokedTokenCount(response.data?.data ?? response.data ?? 0);
    } catch (err) {
      console.error('Error fetching revoked token count:', err);
      setRevokedTokenCount('Error');
    } finally {
      setIsLoadingTokenCount(false);
    }
  };

  const handleCloseTokenModal = () => {
    setShowTokenModal(false);
    setTokenUser(null);
    setRevokedTokenCount(null);
  };

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await userService.getUsers(page, 10);
      // Handle Spring Data Page format
      const data = response.data;
      setUsers(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
      setIsSearching(false);
    } catch (err) {
      toast.error('Failed to load admin users');
      console.error('Error fetching users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    // If no search criteria, fetch all users
    if (!searchQuery && !filterRole && !filterStatus) {
      setPage(0);
      fetchUsers();
      return;
    }

    setIsLoading(true);
    setIsSearching(true);
    try {
      const searchParams = {};
      if (searchQuery) searchParams.query = searchQuery;
      if (filterRole) searchParams.role = filterRole;
      if (filterStatus) searchParams.status = filterStatus;

      const response = await userService.searchUsers(searchParams, 0, 20);
      const data = response.data;
      setUsers(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
      setPage(0);
    } catch (err) {
      toast.error('Failed to search users');
      console.error('Error searching users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterRole('');
    setFilterStatus('');
    setIsSearching(false);
    setPage(0);
    fetchUsers();
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Password change handlers
  const handleOpenPasswordModal = (user) => {
    setPasswordUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordUser(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one digit';
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      return 'Password must contain at least one special character';
    }
    return '';
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    // Validate password requirements
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    setIsChangingPassword(true);
    try {
      await userService.updatePassword(passwordUser.id, newPassword);
      toast.success(`Password changed for ${passwordUser.firstName} ${passwordUser.lastName}`);
      handleClosePasswordModal();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to change password';
      setPasswordError(errorMessage);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        password: '',
        role: user.role,
        status: user.status,
      });
    } else {
      setEditingUser(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'EDITOR',
        status: 'ACTIVE',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: 'EDITOR',
      status: 'ACTIVE',
    });
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      if (editingUser) {
        // For updates, don't send password if empty
        const updateData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          role: formData.role,
          status: formData.status,
        };
        await userService.updateUser(editingUser.id, updateData);
        toast.success('User updated successfully');
      } else {
        // For create, password is required
        if (!formData.password) {
          toast.error('Password is required for new users');
          setIsSaving(false);
          return;
        }
        await userService.createUser(formData);
        toast.success('User created successfully');
      }
      handleCloseModal();
      fetchUsers();
    } catch (err) {
      const errorMessage = err.response?.data?.message || (editingUser ? 'Failed to update user' : 'Failed to create user');
      toast.error(errorMessage);
      console.error('Error saving user:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (user.role === 'SUPER_ADMIN') {
      toast.error('Cannot delete super admin user');
      return;
    }

    if (user.isSyncedFromMain) {
      toast.error('Cannot delete synced users. Update via main backend.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await userService.deleteUser(user.id);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to delete user';
      toast.error(errorMessage);
      console.error('Error deleting user:', err);
    }
  };

  const handleToggleStatus = async (user) => {
    if (user.isSyncedFromMain) {
      toast.error('Cannot modify synced users. Update via main backend.');
      return;
    }

    const newStatus = user.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';

    try {
      await userService.updateUser(user.id, {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
        status: newStatus,
      });
      toast.success(`User ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update user status';
      toast.error(errorMessage);
      console.error('Error updating status:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleBadge = (role) => {
    const roleConfig = ROLES.find((r) => r.value === role) || ROLES[2];
    return (
      <span className="role-badge" style={{ backgroundColor: `${roleConfig.color}15`, color: roleConfig.color }}>
        {roleConfig.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || 'inactive';
    return (
      <span className={`status-badge ${statusLower}`}>
        {status === 'ACTIVE' ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="admin-users-page">
        <div className="page-loading">
          <div className="loading-spinner"></div>
          <span>Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-users-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Admin Users</h1>
          <p>Manage admin accounts and permissions.</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Add User
          </button>
        </div>
      </div>

      {/* User Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon total">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{isLoadingStats ? '-' : (stats?.totalUsers || 0)}</span>
            <span className="stat-label">Total Users</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon active">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{isLoadingStats ? '-' : (stats?.activeUsers || 0)}</span>
            <span className="stat-label">Active Users</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon inactive">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{isLoadingStats ? '-' : (stats?.inactiveUsers || 0)}</span>
            <span className="stat-label">Inactive Users</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon admin">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{isLoadingStats ? '-' : (stats?.adminUsers || 0)}</span>
            <span className="stat-label">Admin Users</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon recent">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{isLoadingStats ? '-' : (stats?.recentLogins || 0)}</span>
            <span className="stat-label">Recent Logins</span>
          </div>
        </div>
      </div>

      {/* Sync Status (SUPER_ADMIN only) */}
      {isSuperAdmin && (
        <div className="sync-status-card">
          <div className="sync-status-header">
            <div className="sync-status-title">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
              </svg>
              <span>User Sync Status</span>
            </div>
            <div className={`sync-connection-badge ${syncStatus?.rabbitMQConnected ? 'connected' : 'disconnected'}`}>
              <span className="sync-dot"></span>
              {isLoadingSyncStatus ? 'Checking...' : (syncStatus?.rabbitMQConnected ? 'RabbitMQ Connected' : 'RabbitMQ Disconnected')}
            </div>
          </div>

          <div className="sync-status-grid">
            <div className="sync-stat">
              <span className="sync-stat-value">{isLoadingSyncStatus ? '-' : (syncStatus?.totalUsers || 0)}</span>
              <span className="sync-stat-label">Total Users</span>
            </div>
            <div className="sync-stat">
              <span className="sync-stat-value">{isLoadingSyncStatus ? '-' : (syncStatus?.syncedUsers || 0)}</span>
              <span className="sync-stat-label">Synced from Main</span>
            </div>
            <div className="sync-stat">
              <span className="sync-stat-value">{isLoadingSyncStatus ? '-' : (syncStatus?.cmsUsers || 0)}</span>
              <span className="sync-stat-label">CMS-Only Users</span>
            </div>
            <div className="sync-stat">
              <span className="sync-stat-value sync-date">{isLoadingSyncStatus ? '-' : formatSyncDate(syncStatus?.lastSyncAt)}</span>
              <span className="sync-stat-label">Last Sync</span>
            </div>
          </div>

          <div className="sync-status-footer">
            <span className="sync-info">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
              </svg>
              Synced users are read-only. Update them via the main backend.
            </span>
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleCleanupTokens}
              disabled={isCleaningTokens}
              title="Clean up expired tokens from the blacklist"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              {isCleaningTokens ? 'Cleaning...' : 'Cleanup Tokens'}
            </button>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="search-filters-container">
        <div className="search-input-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="search-icon">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
        </div>

        <div className="filter-group">
          <select
            className="filter-select"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
          >
            <option value="">All Roles</option>
            {ROLES.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>

          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            {STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>

          <button className="btn btn-primary btn-sm" onClick={handleSearch}>
            Search
          </button>

          {isSearching && (
            <button className="btn btn-secondary btn-sm" onClick={handleClearFilters}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Results count */}
      {isSearching && (
        <div className="search-results-info">
          Found {totalElements} user{totalElements !== 1 ? 's' : ''} matching your criteria
        </div>
      )}

      {/* Users Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  <div className="user-info">
                    <div className="user-avatar">
                      {getInitials(user.firstName, user.lastName)}
                    </div>
                    <div className="user-details">
                      <span className="user-name">{user.firstName} {user.lastName}</span>
                      <span className="user-email">{user.email}</span>
                    </div>
                  </div>
                </td>
                <td>{getRoleBadge(user.role)}</td>
                <td>{getStatusBadge(user.status)}</td>
                <td className="date-cell">{formatDate(user.lastLogin)}</td>
                <td className="text-right">
                  <div className="table-actions">
                    {!user.isSyncedFromMain && (
                      <>
                        <button
                          className="btn-icon"
                          onClick={() => handleOpenModal(user)}
                          title="Edit user"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                          </svg>
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleOpenPasswordModal(user)}
                          title="Change password"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                          </svg>
                        </button>
                      </>
                    )}
                    {user.isSyncedFromMain && (
                      <span className="synced-badge" title="Synced from main backend">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                        </svg>
                      </span>
                    )}
                    {isSuperAdmin && (
                      <button
                        className="btn-icon"
                        onClick={() => handleViewRevokedTokens(user)}
                        title="View revoked tokens"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                        </svg>
                      </button>
                    )}
                    {user.role !== 'SUPER_ADMIN' && !user.isSyncedFromMain && (
                      <>
                        <button
                          className="btn-icon"
                          onClick={() => handleToggleStatus(user)}
                          title={user.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                        >
                          {user.status === 'ACTIVE' ? (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                        </button>
                        <button
                          className="btn-icon danger"
                          onClick={() => handleDelete(user)}
                          title="Delete user"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {page + 1} of {totalPages}
          </span>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
          >
            Next
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">First Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label required">Last Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label required">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">Role</label>
                  <select
                    className="form-select"
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    disabled={editingUser?.role === 'SUPER_ADMIN'}
                  >
                    {ROLES.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                {!editingUser && (
                  <div className="form-group">
                    <label className="form-label required">Password</label>
                    <input
                      type="password"
                      className="form-input"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Min 8 chars, uppercase, lowercase, digit, special char"
                      required
                    />
                    <span className="form-hint">
                      Password must be at least 8 characters with uppercase, lowercase, digit, and special character.
                    </span>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSaving}>
                  {isSaving ? 'Saving...' : editingUser ? 'Save Changes' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && passwordUser && (
        <div className="modal-overlay" onClick={handleClosePasswordModal}>
          <div className="modal password-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Password</h2>
              <button className="modal-close" onClick={handleClosePasswordModal}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleChangePassword}>
              <div className="modal-body">
                <div className="password-user-info">
                  <div className="user-avatar">
                    {getInitials(passwordUser.firstName, passwordUser.lastName)}
                  </div>
                  <div>
                    <p className="user-name">{passwordUser.firstName} {passwordUser.lastName}</p>
                    <p className="user-email">{passwordUser.email}</p>
                  </div>
                </div>

                {passwordError && (
                  <div className="form-error-alert">
                    {passwordError}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label required">New Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    autoComplete="new-password"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">Confirm Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    autoComplete="new-password"
                  />
                </div>

                <div className="password-requirements">
                  <p>Password must contain:</p>
                  <ul>
                    <li className={newPassword.length >= 8 ? 'valid' : ''}>At least 8 characters</li>
                    <li className={/[A-Z]/.test(newPassword) ? 'valid' : ''}>One uppercase letter</li>
                    <li className={/[a-z]/.test(newPassword) ? 'valid' : ''}>One lowercase letter</li>
                    <li className={/[0-9]/.test(newPassword) ? 'valid' : ''}>One digit</li>
                    <li className={/[^A-Za-z0-9]/.test(newPassword) ? 'valid' : ''}>One special character</li>
                  </ul>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleClosePasswordModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isChangingPassword}>
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Token Info Modal (SUPER_ADMIN only) */}
      {showTokenModal && tokenUser && (
        <div className="modal-overlay" onClick={handleCloseTokenModal}>
          <div className="modal token-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Token Information</h2>
              <button className="modal-close" onClick={handleCloseTokenModal}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <div className="token-user-info">
                <div className="user-avatar">
                  {getInitials(tokenUser.firstName, tokenUser.lastName)}
                </div>
                <div>
                  <p className="user-name">{tokenUser.firstName} {tokenUser.lastName}</p>
                  <p className="user-email">{tokenUser.email}</p>
                </div>
              </div>

              <div className="token-stats">
                <div className="token-stat-card">
                  <div className="token-stat-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                  </div>
                  <div className="token-stat-content">
                    <span className="token-stat-value">
                      {isLoadingTokenCount ? (
                        <span className="loading-dots">...</span>
                      ) : revokedTokenCount === 'Error' ? (
                        <span className="error-text">Error</span>
                      ) : (
                        revokedTokenCount
                      )}
                    </span>
                    <span className="token-stat-label">Revoked Tokens</span>
                  </div>
                </div>
              </div>

              <div className="token-info-note">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>
                <p>Revoked tokens are added to a blacklist when users log out or when their sessions are invalidated. Use the "Cleanup Tokens" button to remove expired tokens from the blacklist.</p>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseTokenModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsersPage;
